import { Button } from '@fox/atoms/src/button/index';
import { DepcrecatedSelect } from '@fox/atoms/deprecated/select/index';
import { FIELD_HEIGHT, FIELD_WIDTH } from '@fox/atoms/contracts/field';
import { Icon } from '@fox/atoms/src/icon/index';
import { MuiMenuItem } from '@fox/atoms/src/mui-list/index';
import { Popover } from '@fox/atoms/src/popover/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { strIsNullOrEmpty } from '@fox/atoms/utils/string';
import { FlexRowSmallSpaced, FlexRowSpaced } from '@fox/layout/src/flex';
import { getExtension } from '@fox/extensions/src/extension-context';
import React from 'react';
import { useTheme } from 'styled-components';
import { useAutoDataGridContext } from '../../auto-data-grid/context';
import { GraphColumn, SortState } from '../../auto-data-grid/types';
import { FilterOperatorProps } from '../../contracts/FilterOperatorProps';
import { COLUMN_INPUT_WIDTH } from '../../contracts/filters';

interface SortPopoverProps {
	options: Pick<GraphColumn<any>, 'dataIndex' | 'sortIndex' | 'filterType' | 'title' | 'sortable'>[];
}

enum SortType {
	ASCENDING = 'Ascending',
	DESCENDING = 'Descending'
}

export function SortPopover(props: SortPopoverProps) {
	const columns = props.options;
	const theme = useTheme();
	const { sort, setSortColumn } = useAutoDataGridContext() ?? {};
	const sortApplied = (sort?.asc === true || sort?.asc === false) && sort?.column;

	const [sortColumns, setSortColumns] = React.useState<SortState | undefined>(sort);

	React.useEffect(() => {
		setSortColumns(sort);
	}, [sort]);

	const [vis, setVis] = React.useState(false);

	const overlay = (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<div style={{ padding: '1rem' }}>
				<Typography variant="h6">Sort</Typography>
			</div>
			<div style={{ display: 'flex', gap: '1rem', padding: '0 1rem 1rem', alignItems: 'center' }}>
				<div style={{ width: '3rem' }}>
					<Typography variant="body1">By</Typography>
				</div>
				<DepcrecatedSelect
					style={{ width: FIELD_WIDTH, height: FIELD_HEIGHT }}
					value={sortColumns?.column ? sortColumns?.column : ''}
					multiple={false}
					onChange={e => {
						const target = e.target.value;

						setSortColumns(prevState => ({ ...prevState, column: target }));

						if (sortColumns?.asc === true || sortColumns?.asc === false) {
							setSortColumn?.({ asc: sortColumns.asc, column: target });
							setVis(false);
						}
					}}
				>
					{columns.map(o => {
						if (o.dataIndex === 'addcolumn' || !o.sortable) return;

						// HACK: This is a band-aid around the fact that most usages of the one-table-to-rule-them-all component define sortable
						// columns (sortable: true), but do not specify the sortIndex property. This would normally be fine for most
						// cases, except the List Builder results table where sortIndex is different from dataIndex and this distinction
						// needs to be made (ie. We need to sort on the column indicated by sortIndex, not dataIndex!)
						//
						// To cater for both use cases, we'll assume sortIndex as "the index" and only fall back to dataIndex iff the column
						// does not specify sortIndex and (sortable == true)
						//
						// The true fix is to kill the sortable property, and infer sortability through whether sortIndex is present or not. This
						// fix is very disruptive due to everything that isn't List Builder defining sortable columns completely wrong, so such
						// a fix can be postponed for later.
						let theIndex = o.sortIndex;
						if (!theIndex && o.sortable) {
							theIndex = o.dataIndex;
						}
						return (
							<MuiMenuItem value={theIndex} key={theIndex}>
								<FlexRowSmallSpaced style={{ width: COLUMN_INPUT_WIDTH }}>
									<Icon iconName={getExtension<FilterOperatorProps>(`column-operators-${o.filterType}`)?.icon ?? 'ShortText'} />
									<div
										title={typeof o.title === 'string' ? o.title : undefined}
										style={{
											textOverflow: 'ellipsis',
											overflow: 'hidden',
											whiteSpace: 'nowrap'
										}}
									>
										{o.title}
									</div>
								</FlexRowSmallSpaced>
							</MuiMenuItem>
						);
					})}
				</DepcrecatedSelect>
				<DepcrecatedSelect
					style={{ width: FIELD_WIDTH, height: FIELD_HEIGHT }}
					multiple={false}
					value={sortColumns?.asc === true ? 'Ascending' : sortColumns?.asc === false ? 'Descending' : ''}
					onChange={e => {
						const target = e.target.value;
						setSortColumns(prevState => ({ ...prevState, asc: target === 'Ascending' ? true : false }));
					}}
				>
					<MuiMenuItem value={SortType.ASCENDING} key="asc">
						<FlexRowSpaced>
							<Icon iconName="Sort" sx={{ transform: 'rotate(180deg) scaleX(-1)' }} />
							<div>Ascending</div>
						</FlexRowSpaced>
					</MuiMenuItem>
					<MuiMenuItem value={SortType.DESCENDING} key="des">
						<FlexRowSpaced>
							<Icon iconName="Sort" />
							<div>Descending</div>
						</FlexRowSpaced>
					</MuiMenuItem>
				</DepcrecatedSelect>
			</div>
			<hr style={{ width: '100%', border: '1px solid rgba(0, 0, 0, 0.12)', margin: 0 }} />
			<div style={{ display: 'flex', padding: '1rem' }}>
				<Button
					color="error"
					title="Clear sort"
					style={{ padding: 0! }}
					onClick={() => {
						setSortColumn?.(null);
						setSortColumns(prevState => sort);
						setVis(false);
					}}
				>
					Clear all
				</Button>
				<Button
					variant="primary"
					style={{ marginLeft: 'auto' }}
					title="Apply sort"
					onClick={() => {
						if (sortColumns && !strIsNullOrEmpty(sortColumns?.column)) {
							setSortColumn?.({ column: sortColumns.column, asc: sortColumns.asc });
							setVis(false);
						}
						setVis(false);
					}}
				>
					Apply sort
				</Button>
			</div>
		</div>
	);

	return (
		<Popover overlay={overlay} placement="bottom-start" trigger="click" onVisibleChange={setVis} visible={vis}>
			<Button
				startIcon={<Icon iconName={'Sort'} />}
				//variant={'text'}
				style={{ color: theme.colors.font, backgroundColor: sortApplied ? theme.colors.grey?.[200] : '' }}
				sx={{ '&:hover': { opacity: sortApplied ? '0.8 !important' : 'unset' } }}
			>
				Sort
			</Button>
		</Popover>
	);
}
